import React, { useState } from "react";
import { Row, Col, Card, Progress, List, Typography, message } from "antd";
import {
  Button,
  Flex,
  ExcelColorIcon,
  FilterIcon,
  CheckmarkCircleIcon,
  CloseIcon,
  RetryIcon,
  AcceptIcon,
  Checkbox,
  Text,
  Loader,
} from "@fluentui/react-northstar";
import { useIntelligentGrading } from "../../../../context/useIntelligentGrading";
import StudentGradingCard from "./StudentGradingCard";
import ConfirmDialog from "../common/ConfirmDialog";
import QuickViewDialog from "./QuickViewDialog";
import { useAssignment } from "../../../../../../context/AssignmentContext";
import moment from "moment";
import * as XLSX from "xlsx";
import BatchGradingFilterModel from "./BatchGradingFilterModel";
import { useTranslation } from "react-i18next";

const { Title } = Typography;

const brandColor = "#6264a7";

const ASSIGNMENT_STATUSES = {
  READY_TO_APPROVE: "Ready for Approval",
  NEEDS_REVIEW: "Requires Review",
  UNCLEAR_THESIS: "Unclear Thesis Statement",
  OFF_TOPIC: "Off-Topic Content",
  LATE: "Late Submission",
  HIGH_CONFIDENCE: "High Confidence Score",
  EXEMPLARY: "Exemplary Work",
};

const GRADES = ["A+", "A", "A-", "B+", "B", "F"];
const SUBMISSION_STATUSES = [
  "Pending",
  "Grades Pending",
  "Resubmit",
  "Completed",
];

const BatchGrading = () => {
  const { t } = useTranslation();

  const translation = t("assignment").summary.batch;

  const {
    statusWiseCounts,
    students,
    setStudents,
    handleBulkApprove,
    handleBulkRegenerate,
    submitGrading,
    isLoading,
    allAssignmentSubmissions,
    handleSubmissionChange,
    handleBulkReject,
    handleRegenerate,
    handleExportToExcel,
    overallScore,
    isExporting,
    getOverallScore,
    isFilterOpen,
    handleFilterOpen,
    handleFilterClose,
    getStudentsSubmission,
  } = useIntelligentGrading();

  const { currentAssignmentData } = useAssignment();

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: "", // 'approve', 'regenerate', 'single-approve', 'single-regenerate'
    loading: false,
    submission: null, // for single submission actions
  });

  const [quickViewDialog, setQuickViewDialog] = useState({
    open: false,
    student: null,
  });

  const [selectedFilterParameters, setSelectedFilterParameters] = useState({
    grades: [],
    statuses: [],
  });

  const actionableStudents = students.filter(
    (s) =>
      s.submissionStatus === "Grades Pending" &&
      s.aiGradingStatus === "Completed"
  );
  const actionableIds = actionableStudents.map((s) => s.id);
  const selectedIds = students.filter((s) => s.isSelected).map((s) => s.id);
  const allSelected =
    selectedIds.length === actionableIds.length && actionableIds.length > 0;
  const isIndeterminate =
    selectedIds.length > 0 && selectedIds.length < actionableIds.length;

  const handleSelectAll = (e, data) => {
    setStudents((prev) =>
      prev.map((s) => ({
        ...s,
        isSelected: data.checked && s.submissionStatus === "Grades Pending",
      }))
    );
  };

  const handleSelectOne = (id) => {
    setStudents((prev) =>
      prev.map((s) =>
        s.id === id &&
        s.submissionStatus === "Grades Pending" &&
        s.aiGradingStatus === "Completed"
          ? { ...s, isSelected: !s.isSelected }
          : s
      )
    );
  };

  const openConfirmDialog = (type, submission = null) => {
    setConfirmDialog({
      open: true,
      type,
      loading: false,
      submission,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      type: "",
      loading: false,
      submission: null,
    });
  };

  const handleFetchAttachments = async (submissionId, studentId) => {
    const submission = await getStudentsSubmission(submissionId, studentId);
    setQuickViewDialog((prev) => ({
      ...prev,
      attachments: submission.attachments,
      loading: false,
    }));
  };

  const handleQuickView = (student) => {
    const submission = allAssignmentSubmissions.find(
      (submission) => submission.id === student.id
    );
    handleFetchAttachments(submission.id, submission.studentId);
    setQuickViewDialog({
      open: true,
      loading: true,
      attachments: [],
      student: {
        ...submission,
        ...student,
        gradingResult:
          submission.submissionStatus === "Completed"
            ? submission.teacherGradingResult
            : submission.aiGradingResult,
        overallScore: getOverallScore(student.rubrics),
      },
    });
  };

  const handleCloseQuickView = () => {
    setQuickViewDialog({
      open: false,
      student: null,
      attachments: [],
      loading: false,
    });
  };

  const handleEdit = (student) => {
    // Find the index of the student in allAssignmentSubmissions
    const submissionIndex = allAssignmentSubmissions.findIndex(
      (submission) => submission.id === student.id
    );
    if (submissionIndex !== -1) {
      handleSubmissionChange(submissionIndex);
    }
  };

  const handleConfirm = async () => {
    setConfirmDialog((prev) => ({ ...prev, loading: true }));
    try {
      const type = confirmDialog.type;

      if (type === "single-approve") {
        const submission = allAssignmentSubmissions.find(
          (submission) => submission.id === confirmDialog.submission.id
        );
        await submitGrading(
          "Completed",
          {
            ...submission,
            overallScore,
          },
          "",
          "single-approve"
        );
      } else if (type === "single-regenerate") {
        await handleRegenerate(
          confirmDialog.submission.id,
          confirmDialog.submission.email
        );
      } else if (type === "approve") {
        await handleBulkApprove(selectedIds);
      } else if (type === "regenerate") {
        const payload = {
          assignmentId: currentAssignmentData.id,
          submissions: students
            .filter((s) => s.isSelected)
            .map((s) => {
              return {
                id: s.id,
                studentId: s.email,
              };
            }),
        };
        await handleBulkRegenerate(payload);
      } else if (type === "reject") {
        await handleBulkReject(selectedIds);
      } else if (type === "approve-high-confidence") {
        const { confidenceThreshold } = currentAssignmentData.settings;
        const highConfidenceIds = students
          .filter(
            (s) =>
              s.confidence >= confidenceThreshold &&
              s.submissionStatus === "Grades Pending"
          )
          .map((s) => s.id);
        await handleBulkApprove(highConfidenceIds);
      }
    } finally {
      closeConfirmDialog();
    }
  };

  const handleApproveHighConfidence = () => {
    const highConfidenceSubmissions = students.filter(
      (s) =>
        s.confidence >= currentAssignmentData?.settings?.confidenceThreshold &&
        s.submissionStatus === "Grades Pending"
    );

    if (highConfidenceSubmissions.length === 0) {
      // You might want to show a message that there are no high confidence submissions
      return;
    }

    openConfirmDialog("approve-high-confidence");
  };

  const getDialogTitle = () => {
    const type = confirmDialog.type;
    if (type === "single-approve" || type === "approve") {
      return translation.dialog.confirmAssignmentApproval;
    }
    if (type === "reject") {
      return translation.dialog.confirmAssignmentRejection;
    }
    if (type === "approve-high-confidence") {
      return translation.dialog.confirmHighConfidenceApprovals;
    }
    return translation.dialog.confirmFeedbackRegeneration;
  };

  const getDialogContent = () => {
    const type = confirmDialog.type;
    const isSingle = type.startsWith("single-");
    let action;
    if (type.includes("approve")) {
      action = "approve";
    } else if (type === "reject") {
      action = "reject";
    } else {
      action = "regenerate feedback for";
    }

    if (isSingle) {
      return translation.dialog.approveSingleSubmission.replace(
        "{name}",
        confirmDialog.submission.name
      );
    }
    if (type === "approve-high-confidence") {
      const count = students.filter(
        (s) =>
          s.confidence >=
            currentAssignmentData?.settings?.confidenceThreshold &&
          s.submissionStatus === "Grades Pending"
      ).length;
      return translation.dialog.approveHighConfidenceSubmissions.replace(
        "{count}",
        count
      );
    }
    return translation.dialog.approveSelectedSubmissions.replace(
      "{count}",
      selectedIds.length
    );
  };

  const formatSubmissionDate = (date) => {
    if (!date) return "";
    return moment(date).format("DD-MM-YYYY");
  };

  const onResetFilter = () => {
    setSelectedFilterParameters({
      grades: [],
      statuses: [],
    });
  };

  const onApplyFilter = () => {
    // Filter the students based on selected parameters
    const filteredStudents = students.filter((student) => {
      const gradeMatch =
        selectedFilterParameters.grades.length === 0 ||
        selectedFilterParameters.grades.includes(student.grade);

      const statusMatch =
        selectedFilterParameters.statuses.length === 0 ||
        selectedFilterParameters.statuses.includes(student.submissionStatus);

      return gradeMatch && statusMatch;
    });

    setStudents(filteredStudents);
  };

  return (
    <div style={{ padding: 16, background: "#f0f2f5" }}>
      {/* Header */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 16 }}
        gutter={[16, 16]}
      >
        <Col xs={24} md={16}>
          <Title level={4} style={{ margin: 0 }}>
            {translation.header.title}
          </Title>
          <Text type="secondary">
            {statusWiseCounts.total} {translation.header.totalSubmissions} |{" "}
            {translation.header.dueDate}:{" "}
            {moment(currentAssignmentData.dueDate).format("MMM D, YYYY")}
          </Text>
        </Col>
        <Col xs={24} md={8} style={{ textAlign: "right" }}>
          <Flex gap="gap.small" hAlign="end">
            <Button
              icon={<FilterIcon />}
              content={translation.header.filter}
              onClick={handleFilterOpen}
            />
          </Flex>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Sidebar */}

        <Col
          xs={24}
          md={6}
          lg={5}
          style={{
            maxHeight: "calc(100vh - 395px)",
            overflowY: "auto",
          }}
        >
          <div style={{ height: "100%" }}>
            <Card style={{ marginBottom: 16 }}>
              <Title level={5}>{translation.progress.title}</Title>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "16px 0",
                }}
              >
                <Progress
                  type="circle"
                  percent={
                    (statusWiseCounts.approved / statusWiseCounts.total) * 100
                  }
                  width={80}
                  strokeColor={brandColor}
                />
              </div>
              <List size="small" style={{ marginBottom: 8 }}>
                <List.Item>
                  <Text>{translation.progress.totalSubmissions}</Text>
                  <Text strong>{statusWiseCounts.total}</Text>
                </List.Item>
                <List.Item>
                  <Text>{translation.progress.approvedCompleted}</Text>
                  <Text strong type="success">
                    {statusWiseCounts.approved}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text>{translation.progress.awaitingReview}</Text>
                  <Text strong style={{ color: "#6264a7" }}>
                    {statusWiseCounts.gradesPending}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text>{translation.progress.pendingSubmission}</Text>
                  <Text strong type="warning">
                    {statusWiseCounts.pending}
                  </Text>
                </List.Item>
              </List>
              <Button
                icon={<ExcelColorIcon />}
                content={
                  isExporting
                    ? translation.progress.preparingExport
                    : translation.progress.exportResults
                }
                fluid
                style={{ marginBottom: 8 }}
                onClick={handleExportToExcel}
                disabled={
                  isExporting ||
                  allAssignmentSubmissions.length === 0 ||
                  isLoading
                }
                loading={isExporting}
              />
            </Card>
            <Card>
              <Title level={5}>{translation.actions.bulkActions}</Title>
              <Button
                icon={<CheckmarkCircleIcon />}
                data-cy="approve-high-confidence"
                primary
                fluid
                style={{
                  marginBottom: 8,
                  background: "#52c41a",
                  borderColor: "#52c41a",
                }}
                content={translation.actions.approveHighConfidence}
                onClick={handleApproveHighConfidence}
                disabled={
                  isLoading ||
                  !students.some(
                    (s) =>
                      s.confidence >=
                        currentAssignmentData?.settings?.confidenceThreshold &&
                      s.submissionStatus === "Grades Pending"
                  )
                }
              />
              <Button
                icon={<RetryIcon />}
                data-cy="regenerate-bulk-ai-feedback"
                content={translation.actions.regenerateAiFeedback}
                fluid
                onClick={() => openConfirmDialog("regenerate")}
                disabled={
                  isLoading ||
                  !students.some((s) => s.submissionStatus === "Grades Pending")
                }
              />
            </Card>
          </div>
        </Col>

        {/* Main Content */}
        <Col xs={24} md={18} lg={19}>
          <Card style={{ marginBottom: 16 }}>
            <Row align="middle" justify="space-between" gutter={[8, 8]}>
              <Col>
                <Checkbox
                  data-cy="select-all-gradable-submissions"
                  label={`${translation.actions.selectAllGradable} (${actionableIds.length} ${translation.actions.available})`}
                  checked={allSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                  disabled={actionableIds.length === 0}
                />
              </Col>
              <Col>
                <Flex gap="gap.small">
                  <Button
                    primary
                    icon={<AcceptIcon />}
                    data-cy="approve-selected-submissions"
                    content={translation.actions.approveSelected}
                    style={{ background: "#52c41a", borderColor: "#52c41a" }}
                    onClick={() => openConfirmDialog("approve")}
                    disabled={isLoading || selectedIds.length === 0}
                  />
                  <Button
                    data-cy="reject-selected-submissions"
                    icon={<CloseIcon />}
                    content={translation.actions.rejectSelected}
                    onClick={() => openConfirmDialog("reject")}
                    disabled={isLoading || selectedIds.length === 0}
                  />
                </Flex>
              </Col>
            </Row>
          </Card>
          <Row
            gutter={[16, 16]}
            style={{
              maxHeight: "calc(100vh - 491px)",
              paddingBottom: "20px",
              overflowY: "auto",
            }}
          >
            {students.map((student) => (
              <Col xs={24} sm={24} md={12} lg={8} key={student.id}>
                <StudentGradingCard
                  student={student}
                  isSelected={student.isSelected}
                  onClick={() => handleSelectOne(student.id)}
                  onApprove={() => openConfirmDialog("single-approve", student)}
                  onRegenerate={() =>
                    openConfirmDialog("single-regenerate", student)
                  }
                  onEdit={() => handleEdit(student)}
                  onQuickView={() => handleQuickView(student)}
                  disabled={student.submissionStatus !== "Grades Pending"}
                />
              </Col>
            ))}
          </Row>
          {/* <div style={{ marginTop: 24, textAlign: "right" }}>
            <Text>Showing 1 to 6 of 32 submissions</Text>
            <Pagination
              current={1}
              total={32}
              pageSize={6}
              style={{ marginLeft: 16, display: "inline-block" }}
            />
          </div> */}
        </Col>
      </Row>

      <ConfirmDialog
        open={confirmDialog.open}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
        title={getDialogTitle()}
        content={getDialogContent()}
        confirmText={
          confirmDialog.type?.includes("approve")
            ? translation.dialog.approve
            : confirmDialog.type === "reject"
            ? translation.dialog.reject
            : translation.dialog.regenerate
        }
        cancelText={translation.dialog.cancel}
        loading={confirmDialog.loading}
      />

      <QuickViewDialog
        open={quickViewDialog.open}
        onClose={handleCloseQuickView}
        student={quickViewDialog.student}
        attachments={quickViewDialog.attachments}
        loading={quickViewDialog.loading}
      />

      <BatchGradingFilterModel
        open={isFilterOpen}
        onHandlePortal={handleFilterClose}
      />
    </div>
  );
};

export default BatchGrading;
